/**
 * Como uma app aberta apanha um deploy novo.
 *
 * O browser só procura um service worker novo ao abrir a app ou numa navegação; uma PWA
 * deixada aberta dias num telemóvel nunca o fazia. Aqui procura-se de 10 em 10 minutos e
 * sempre que a app volta a ficar visível. Com `registerType: 'autoUpdate'`, o SW novo
 * assume sozinho e a página recarrega.
 *
 * Sem service worker (browser sem suporte, modo privado, SW desligado) não há essa via:
 * lê-se `/version.json` (escrito a cada build) e, se a versão difere, recarrega-se
 * quando a app fica escondida, para nunca cortar o que a pessoa está a fazer.
 */

const INTERVALO_MS = 10 * 60 * 1000;

export function versaoMudou(local: string, remota: string | null): boolean {
  // `unknown` é um build sem SHA (dev local): nada com que comparar.
  return remota !== null && remota !== "unknown" && local !== "unknown" && remota !== local;
}

/** A versão publicada agora, ou `null` se não der para saber (offline, HTML no lugar do JSON...). */
export async function lerVersaoPublicada(pedir: typeof fetch = fetch): Promise<string | null> {
  try {
    const resposta = await pedir("/version.json", { cache: "no-store" });
    if (!resposta.ok || !(resposta.headers.get("content-type") ?? "").includes("json")) return null;
    const corpo: unknown = await resposta.json();
    const versao = (corpo as { version?: unknown } | null)?.version;
    return typeof versao === "string" ? versao : null;
  } catch {
    return null;
  }
}

interface Documento {
  visibilityState: string;
  addEventListener(tipo: "visibilitychange", ouvinte: () => void): void;
}

interface Opcoes {
  /** O registo do service worker, quando existe (só é conhecido depois de `registerSW`). */
  obterRegisto: () => { update(): Promise<unknown> } | undefined;
  versaoLocal?: string;
  lerVersao?: () => Promise<string | null>;
  recarregar?: () => void;
  doc?: Documento;
  intervaloMs?: number;
}

export function iniciarVigiaDeVersao({
  obterRegisto,
  versaoLocal = import.meta.env.VITE_APP_VERSION ?? "unknown",
  lerVersao = lerVersaoPublicada,
  recarregar = () => window.location.reload(),
  doc = document,
  intervaloMs = INTERVALO_MS,
}: Opcoes): void {
  let recarregarAoEsconder = false;

  const verificar = async () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    const registo = obterRegisto();
    if (registo) {
      await registo.update().catch(() => {});
      return;
    }
    if (versaoMudou(versaoLocal, await lerVersao())) recarregarAoEsconder = true;
  };

  setInterval(() => void verificar(), intervaloMs);
  doc.addEventListener("visibilitychange", () => {
    if (doc.visibilityState !== "hidden") void verificar();
    else if (recarregarAoEsconder) recarregar();
  });
}
